import { AlertTriangle } from "lucide-react";
import { RefObject, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAgentRuns } from "@/hooks/agent/useAgentRuns";
import { useInfiniteScrollUp } from "@/hooks/useInfiniteScrollUp";
import { cn } from "@/lib/utils";
import { AgentMessage } from "@/models/agent/message";
import { getAgentThreadStore } from "@/stores/agent/agentThreadStore";
import MessageItemUI from "./MessageItemUI";
import { MessageSkeleton } from "./MessageSkeleton";

interface MessageListUIProps {
  threadId: string;
  messages: AgentMessage[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  className?: string;
  scrollRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
}

const MessageListUI: React.FC<MessageListUIProps> = ({
  threadId,
  messages,
  isLoading,
  isLoadingMore,
  hasMore,
  loadMore,
  className,
  scrollRef,
  messagesEndRef,
}) => {
  const { t } = useTranslation("voiceek_ui");
  const { latestAgentRun } = useAgentRuns(threadId);
  const latestAgentStatus = getAgentThreadStore(threadId)(state => state.latestAgentStatus);
  const hasScrollSpacer = getAgentThreadStore(threadId)(state => state.hasScrollSpacer);
  const setHasScrollSpacer = getAgentThreadStore(threadId)(state => state.setHasScrollSpacer);
  const openToolPanel = getAgentThreadStore(threadId)(state => state.openToolPanel);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [prevMessageLength, setPrevMessageLength] = useState(0);

  const [containerHeight, setContainerHeight] = useState(0);

  useLayoutEffect(() => {
    const el = scrollRef?.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollRef]);

  const isSpacerActive = hasScrollSpacer || !!latestAgentRun?.isRunning;
  const spacerHeight = isSpacerActive ? Math.max(containerHeight - 100, 0) : 100;

  const { sentinelRef } = useInfiniteScrollUp({
    containerRef: scrollRef,
    onLoadMore: loadMore,
    disabled: isLoadingMore || !hasMore,
  });

  const handleToolClick = (toolMessage: AgentMessage) => {
    openToolPanel?.(toolMessage.id);
  };

  useLayoutEffect(() => {
    const isFirstLoad = prevMessageLength === 0 && messages.length > 0;
    if (isFirstLoad && containerHeight > 0) {
      setHasScrollSpacer?.(false);
      scrollRef?.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "auto" });
      setPrevMessageLength(messages.length);
    } else if (!isFirstLoad) {
      setPrevMessageLength(messages.length);
    }
  }, [messages.length, prevMessageLength, scrollRef, setHasScrollSpacer, containerHeight]);

  const reversedMessages = useMemo(() => messages.toReversed(), [messages]);

  // 最後のユーザーメッセージのIDを特定
  const lastUserMessageId = useMemo(
    () => reversedMessages.findLast((msg) => msg.role === "user")?.id ?? null,
    [reversedMessages],
  );

  return (
    <div className={cn(className)}>
      {/* 上スクロール検知用のセンチネル要素 */}
      <div ref={sentinelRef} className="h-4" />

      {/* Loading Indicator */}
      {isLoading
        ? <MessageSkeleton count={3} className="mt-5" />
        : (
          <div className="space-y-4" ref={messagesContainerRef}>
            {reversedMessages.map((message) => (
              <div key={message.id} ref={message.id === lastUserMessageId ? messagesEndRef : undefined}>
                <MessageItemUI
                  message={message}
                  className="mt-5 scroll-mt-[-20px]"
                  onToolClick={handleToolClick}
                />
              </div>
            ))}

            {latestAgentRun?.isRunning && (
              <div className="flex justify-start items-start">
                <div className="flex items-center space-x-1 text-gray-500">
                  <div className="font-normal animate-pulse">
                    {latestAgentStatus?.content ?? "Thinking"}
                    <span className="ml-[2px] tracking-widest">...</span>
                  </div>
                </div>
              </div>
            )}

            {latestAgentRun?.isFailed && (
              <div className="flex justify-start items-center">
                <div className="flex max-w-[80%] items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold">{t("agent.executionError")}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      <div style={{ height: spacerHeight }} />
    </div>
  );
};

export default MessageListUI;
